import React, { useState, useMemo } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { courseShellStatusFilters } from "../filterConfigurations";

const CourseShellStatus = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Course Shell Status"]?.edit;

  // Use Firestore hook, order by Name ascending. Collection name "CourseShellStatus"
  const {
    data: courseShellStatuses,
    loading,
    error,
  } = useFirestore("CourseShellStatus", "Name", "asc");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  const pageName = "Course Shell Status";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(courseShellStatuses || [], courseShellStatusFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (params) => {
    if (canEdit) {
      console.log(
        "CourseShellStatus Main - Row clicked, params.row:",
        params.row,
      );
      setSelectedStatus(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedStatus(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
    // Data will refresh automatically due to onSnapshot in useFirestore
  };

  const handleRecordAdded = () => {
    // Data will refresh automatically due to onSnapshot in useFirestore
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
    // Data should refresh automatically via useFirestore's onSnapshot.
  };

  const columns = useMemo(
    () => [{ field: "Name", headerName: "Name", flex: 1, minWidth: 200 }],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    const processedRows = filteredData.map((status, index) => ({
      id: status.id || index, // Use status.id if available, otherwise use index
      ...status,
    }));

    console.log("CourseShellStatus Main - Sample row data:", processedRows[0]);
    return processedRows;
  }, [filteredData]);

  const exportColumnOrder = columns.map((col) => col.field);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching course shell status:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={courseShellStatusFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="Course Shell Status Filters"
          resultCount={filteredData?.length || 0}
          totalCount={courseShellStatuses?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between", // Adjust as needed for layout
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap", // Allow wrapping on smaller screens
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="CourseShellStatus"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={courseShellStatuses}
              booleanFields={[]}
              stringBooleanFields={[]}
              templateFields={exportColumnOrder}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="CourseShellStatus" />
          )}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <DataGridPro
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: SimpleToolbar }}
            slotProps={{
              toolbar: {
                fileName: "course-shell-status",
              },
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedStatus && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleDialogClose}
          statusDetail={selectedStatus}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CourseShellStatus;
